/**
 * Shared table primitives for the billing screens.
 *
 * The design sweep turned every entity list into cards (see `cards.tsx`), but
 * the billing screens are not entity lists — they are numeric comparisons.
 * "Which brokerage is costing us more than it pays?" is answered by reading
 * DOWN a column, and a stack of cards cannot be read that way. So these stay
 * tables, and what the sweep owes them is the design system: one surface, one
 * header treatment, and figures that line up.
 *
 * Four screens had each grown their own `Th`/`Td`, already drifting in padding
 * and in whether a header could right-align at all — the same three-copy
 * pattern that produced `cards.tsx`.
 */

import type { CSSProperties, ReactNode } from "react";

/**
 * A table's scroll container. Wide content must scroll in its own box —
 * otherwise a twelve-column cost table drags the whole PAGE sideways and the
 * sidebar goes with it.
 */
export function TableScroll({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) {
  return <div style={{ overflowX: "auto", ...style }}>{children}</div>;
}

/** The table element itself. Pair with `TableScroll`. */
export const tableShell: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  // Numbers that change every refresh must not re-flow the columns under the
  // reader's eyes, and a money column is only scannable when the digits line
  // up — both come from tabular figures.
  fontVariantNumeric: "tabular-nums",
};

/** Header row — a hairline under it, not a heavy rule. */
export const theadRow: CSSProperties = {
  textAlign: "left",
  borderBottom: "1px solid var(--rp-color-border)",
};

export function Th({
  children,
  numeric,
}: {
  children?: ReactNode;
  /** Right-align: a figure column reads from its last digit, not its first. */
  numeric?: boolean;
}) {
  return (
    <th
      style={{
        padding: "8px 12px",
        textAlign: numeric ? "right" : "left",
        fontWeight: 600,
        fontSize: 12,
        color: "var(--rp-color-text-muted)",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  numeric,
  bold,
  muted,
}: {
  children?: ReactNode;
  numeric?: boolean;
  bold?: boolean;
  muted?: boolean;
}) {
  return (
    <td
      style={{
        padding: "10px 12px",
        fontSize: 14,
        textAlign: numeric ? "right" : "left",
        fontWeight: bold ? 600 : undefined,
        color: muted ? "var(--rp-color-text-muted)" : undefined,
      }}
    >
      {children}
    </td>
  );
}

/** Body row separator, so every screen draws the same hairline. */
export const bodyRow: CSSProperties = {
  borderTop: "1px solid var(--rp-color-border)",
};
